import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import {
  AppRoutes,
  FundService,
  IDEMPOTENT_WRITE_PATH,
  IdempotencyKeyService,
} from '@gs-2.0/core';
import { FundDisplay, Step } from '@gs-2.0/shared';
import { TransactionService } from '../../../_core/api/transaction.service';
import FundTypes from '../../../_core/constants/fund-types';
import PageTitles from '../../../_core/constants/page-titles';
import { SERVER_ERROR } from '../../../_core/constants/ServerError';
import Urls from '../../../_core/constants/urls';
import SharedMethodsHelpers from '../../../_core/helpers/shared-methods.helpers';
import { GrantDataFee, GrantTransferData } from '../../../_core/models/grantData';
import { Fund } from '../../../_core/models/fund';
import { GlobalConfigApiService } from '../../../_core/api/global-config.service';
import { TitleService } from '../../../_core/services/title.service';
import { FundService as PortalFundService } from '../../../_core/services/fund.service';
import { FeatureFlagService } from '../../../_core/services/feature-flag.service';
import { FeatureFlagKeys } from '../../../_core/models/FeatureFlags';

@Component({
  selector: 'app-distribution',
  templateUrl: './distribution.component.html',
  styleUrls: ['./distribution.component.scss'],
  standalone: false,
})
export class DistributionComponent implements OnInit, OnDestroy {
  urls = Urls;
  currentFund: Fund;
  grantFormData: any;
  grantFormValid = false;
  grantDataFee: GrantDataFee;

  grantDisplayData = new GrantTransferData();

  stepperSteps: Step[] = [
    { label: 'contribute.step.details', number: 1 },
    { label: 'contribute.step.summary', number: 2 },
    { label: 'contribute.step.confirmation', number: 3 },
  ];

  currentStep = 1;
  showError = false;
  isMinAmountError = false;
  loading = false;
  pageLoading = false;
  fundTypes = FundTypes;

  currencyMismatchErrorMsg: string | null = null;
  currencySymbol = '';
  currencyCode = '';
  grantingCurrencyBalance: number | null = null;
  formDisabled = false;
  validating = false;
  recurringEnabled = false;

  numberFormatConfig = { decimalSymbol: '.', digitGroupingSymbol: ',', digitGrouping: '123,456,789' };

  private fundSubscription: Subscription;
  private recurringSubscription: Subscription;

  get activeStepIndex(): number {
    return this.currentStep - 1;
  }

  onFundChanged(fund: FundDisplay): void {
    this.fundService.setSelectedFund(fund.id);
    this.router.navigate([AppRoutes.FUND_DASHBOARD]);
  }

  private triggerFundChanged(): void {
    if (!this.currentFund) {
      this.portalFundService.fundChanged(null);
      return;
    }
    this.portalFundService.fundChanged(this.currentFund);
  }

  constructor(
    private router: Router,
    private titleService: TitleService,
    protected fundService: FundService,
    private portalFundService: PortalFundService,
    private transactionService: TransactionService,
    private featureFlagService: FeatureFlagService,
    private globalConfigApiService: GlobalConfigApiService,
    private idempotencyKeyService: IdempotencyKeyService
  ) {}

  ngOnInit(): void {
    // Opening the form starts a new submission, so no key held over from an earlier attempt
    // may be reused.
    this.idempotencyKeyService.release(IDEMPOTENT_WRITE_PATH.FUND_TO_FUND_TRANSFER);
    this.loadNumberFormat();
    this.recurringSubscription = this.featureFlagService.hasFeatureFlag(FeatureFlagKeys.RecurringDistributions).subscribe((enabled) => {
      this.recurringEnabled = enabled;
    });

    this.fundSubscription = this.portalFundService.fund$.subscribe((fund) => {
      this.fundService.parentFundExpired.set(fund?.parentFund?.expired ?? false);
    });

    const fund = this.fundService.selectedFund();
    this.currentFund = fund as unknown as Fund;
    if (!this.currentFund) {
      this.pageLoading = true;
      this.fundService.loadFunds().then(() => {
        this.currentFund = this.fundService.selectedFund() as unknown as Fund;
        this.pageLoading = false;
        this.setPageTitle();
        this.triggerFundChanged();
        this.checkForCurrencyMismatch();
      });
    } else {
      this.setPageTitle();
      this.triggerFundChanged();
      this.checkForCurrencyMismatch();
    }
  }

  private loadNumberFormat(): void {
    this.globalConfigApiService.getConfiguration().subscribe({
      next: (config: any) => {
        if (config?.response?.numberFormat) {
          this.numberFormatConfig = {
            decimalSymbol: config.response.numberFormat.decimalSymbol?.value || '.',
            digitGroupingSymbol: config.response.numberFormat.digitGroupingSymbol?.value || ',',
            digitGrouping: config.response.numberFormat.digitGrouping || '123,456,789',
          };
        }
      },
    });
  }

  ngOnDestroy(): void {
    this.fundSubscription?.unsubscribe();
    this.recurringSubscription?.unsubscribe();
  }

  setPageTitle(): void {
    if (!this.currentFund) return;
    this.currentFund.fundType === FundTypes.CHARITY
      ? this.titleService.titleChanged(PageTitles.GRANT_CHARITY)
      : this.titleService.titleChanged(PageTitles.GRANT_DAF);
  }

  checkForCurrencyMismatch(): void {
    if (!this.currentFund || this.currentFund.fundType !== FundTypes.CHARITY) return;

    const grantingSymbol = this.currentFund.receiveGrantCurrencySymbol;
    if (!grantingSymbol) return;

    // POR-7178: every charity distributes from its granting currency's balance, so that balance is always
    // looked up. This used to run only when currency !== receiveGrantCurrency, which assumes the money is
    // held in the fund's own currency. A fund whose own and granting currencies are both CHF but whose money
    // sits in USD skipped the check, and the form showed currentFund.availableAmount (the USD amount) under
    // the CHF label.
    this.currencySymbol = grantingSymbol;
    this.currencyCode = grantingSymbol;
    this.validating = true;

    // cseg5 exists only on funds migrated from the old system; fundNumber is always present. Balance
    // endpoints must be given whichever the fund actually has, which is what getFundBalanceId resolves
    // (cseg5 || fundNumber) -- a raw cseg5 is empty for a BT-native fund.
    this.transactionService.getCurrencies(this.fundService.getFundBalanceId()).subscribe({
      next: (currencies: any[]) => {
        const balances = currencies ?? [];
        // Match by symbol (ISO code), not id: receiveGrantCurrency is a local NetSuite numeric id on the
        // DB path, but get-balance-currencies keys its entries by ISO/BT code for a migrated tenant.
        // Matching by id silently fell through to the zero-balance fallback below for every
        // migrated-tenant charity, which incorrectly blocked the distribution with the "FX conversion
        // needed" error even when the fund actually had a healthy balance in its granting currency.
        // A granting currency missing from the list has no balance.
        const grantingCurrency = balances.find((c: any) => c.symbol === grantingSymbol);
        const availableAmount = grantingCurrency?.availableAmountFundCash ?? 0;
        this.grantingCurrencyBalance = availableAmount;

        if (availableAmount <= 0) {
          // No balance in granting currency
          this.currencyMismatchErrorMsg = 'distribution.warning.currency_not_allowed';
          this.formDisabled = true;
          this.validating = false;
          return;
        }

        // The minimum check below is for holding and granting currencies that differ (as in the legacy
        // portal): the granting balance may be an FX'd remainder too small to distribute. A fund whose
        // granting currency is its own and that holds nothing in any other currency has nothing to convert.
        const holdsOtherCurrency = balances.some(
          (c: any) => c.symbol !== grantingSymbol && (c.availableAmountFundCash > 0 || c.balance > 0)
        );
        if (this.currentFund.currency === this.currentFund.receiveGrantCurrency && !holdsOtherCurrency) {
          this.validating = false;
          return;
        }

        // Has balance — verify minimum via calculateGrant
        this.transactionService.calculateGrant({
          amount: availableAmount,
          currency: grantingSymbol,
          senderFundId: this.currentFund.id || this.currentFund.cseg5,
        }).subscribe({
          next: () => {
            this.validating = false;
          },
          error: (error: any) => {
            // POR-7153: no grant contribution allocation exists for this fund/charity pair -- match
            // the legacy system and let the distribution proceed without fees instead of blocking it.
            if (SharedMethodsHelpers.isGrantContributionAllocationNotFound(error)) {
              this.validating = false;
              return;
            }
            if (error?.status === 409 && error?.error?.minimumGrantAmount) {
              this.currencyMismatchErrorMsg = 'distribution.warning.insufficient_balance';
            }
            this.formDisabled = true;
            this.validating = false;
          },
        });
      },
      error: () => {
        // Without the granting currency's balance the form would fall back to the holding balance under
        // the granting label, so block it, as the legacy portal does.
        this.grantingCurrencyBalance = 0;
        this.formDisabled = true;
        this.validating = false;
      },
    });
  }

  computeDisplayData(): void {
    this.grantDisplayData = { ...this.grantFormData };
    this.grantDisplayData.privacy = this.grantFormData.privacy ? 'Yes' : 'No';
    this.grantDisplayData.isRecurring = this.grantFormData.timing === 'recurring';
    this.grantDisplayData.timing =
      this.grantFormData.timing === 'specificDate'
        ? this.grantFormData.date
        : 'As soon as possible';
  }

  goToSummary(): void {
    if (this.grantFormValid) {
      window.scrollTo(0, 0);
      this.computeDisplayData();
      this.currentStep = 2;
    }
  }

  goToForm(): void {
    window.scrollTo(0, 0);
    this.currentStep = 1;
    this.showError = false;
    this.isMinAmountError = false;
  }

  onSubmit(): void {
    this.loading = true;
    const payload = this.buildDistributionPayload(this.grantFormData, this.currentFund);
    this.transactionService.submitTransactionFundToFund(payload).subscribe({
      next: () => {
        // Distribution changed fund balance — refresh so the dashboard's
        // next visit reflects the post-distribution state instead of the
        // F2-cached pre-distribution balances.
        this.fundService.loadFunds({ force: true });
        // get-activity needs a currency: it is now routed to the gateway for migrated tenants, and BT
        // rejects a request without one. `currencyCode` (ISO) is what that path reads, `currency` (the
        // local numeric id) is the DB path's fallback, and `fundNumber` wins over `cseg5` as the fund
        // identifier. All three are additive -- the DB path keeps keying off cseg5 and currency.
        this.transactionService
          .getSummaryAndCurrencies({
            cseg5: this.currentFund.cseg5,
            fundNumber: this.currentFund.fundNumber,
            currency: this.currentFund.currency,
            currencyCode: this.currentFund.currencySymbol,
          })
          .subscribe();
        this.currentStep = 3;
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        if (error.status === 409 && error.error?.message === SERVER_ERROR.NOT_MINIMUM_AMOUNT) {
          this.isMinAmountError = true;
        } else {
          this.showError = true;
        }
      },
    });
  }

  goToPending(): void {
    this.router.navigate([`${Urls.APP}/${Urls.ACTIVITY}/${Urls.PENDING}`]);
  }

  goToDashboard(): void {
    this.router.navigate([`${Urls.FUND}/${Urls.DASHBOARD}`]);
  }

  reloadDistribution(): void {
    const currentUrl = this.router.url;
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = 'reload';
    this.router.navigate([currentUrl]);
  }

  private buildDistributionPayload(formData: any, fund: Fund): any {
    const isRecurring = formData.timing === 'recurring';
    return {
      isFromGrantsTab: false,
      senderCseg5: fund.id,
      senderFundName: fund.fundName,
      receiverCseg5: formData.receiverCseg5,
      receiverFundName: formData.receiverFundName,
      // Fund numbers for the API Gateway path, sent alongside — not instead of — the cseg5 fields the
      // NetSuite/database path still needs. BT identifies funds by fundNumber (it matches
      // vault.fundNumber and returns FUND_NOT_FOUND otherwise), and the backend needs one from the
      // client as the lookup key: it cannot resolve a fund number server-side before it has fetched the
      // fund. cseg5 is legacy and absent for newer funds, so without these a distribution cannot be
      // routed or resolved for such a fund.
      senderFundNumber: fund.fundNumber,
      // Kept in step with receiverCseg5 above, which does NOT prefer a selected project here (unlike
      // grant.component.ts). If distributions should target the project when one is chosen, both this
      // and receiverCseg5 need to change together.
      receiverFundNumber: formData.receiverFundNumber ?? null,
      // ISO code, not the numeric id. `this.currencyCode` is misnamed — checkForCurrencyMismatch
      // assigns it receiveGrantCurrency (an id), and only for CHARITY funds, so this previously sent
      // the id in every other case. The *Symbol fields are the ISO codes: BT identifies currency by ISO
      // and rejects a numeric id ("currency 5 is not a registered currency"). Prefer the grant-receiving
      // currency when the fund has a distinct one, else the fund's own.
      currency: fund.receiveGrantCurrencySymbol || fund.currencySymbol,
      amount: formData.amount,
      date: SharedMethodsHelpers.dateToMilliseconds(formData.date, true),
      privacy: formData.privacy ? formData.privacy : false,
      charityInstruction: formData.charityInstruction ? formData.charityInstruction : '',
      additionalNotes: formData.additionalNotes,
      charityProject: fund.fundType === FundTypes.CHARITY || fund.fundType === FundTypes.PROJECT,
      fundType: fund.fundType,
      amountGrantAssessments: this.grantDataFee?.calculatedGrantAssessmentExpense ?? 0,
      amountProcessingAssessments: this.grantDataFee?.processingAssessmentExpense ?? 0,
      amountSupGrantAssessments: this.grantDataFee?.supplementalGrantAssessment ?? 0,
      isRecurring,
      recurrencePattern: isRecurring ? (formData.recurrencePattern ?? null) : null,
      startDate: isRecurring ? (formData.startDate ?? null) : null,
      endDate: isRecurring ? (formData.endDate ?? null) : null,
      timeZone: this.getTimezone(),
    };
  }

  private getTimezone(): string {
    const offset = -new Date().getTimezoneOffset();
    const sign = offset >= 0 ? '+' : '-';
    const hours = Math.floor(Math.abs(offset) / 60).toString().padStart(2, '0');
    const mins = (Math.abs(offset) % 60).toString().padStart(2, '0');
    return `UTC${sign}${hours}:${mins}`;
  }
}
